import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, FormSection, Field } from '../patterns';
import { quickCreateConsumable } from '../../services/procurementService';
import type { ConsumableBase, ConsumableType } from '../../services/consumableService';

/**
 * Quick-create a Consumable (chemical / ppe / fertilizer / diesel) without
 * leaving the procurement form. Spec B Task 7.1.
 *
 * The modal collects the minimum fields required by
 * `POST /api/v1/consumables/quick-create`:
 *   - consumableType (4-way radio, defaults to `defaultType` or `chemical`)
 *   - productName    (required)
 *   - category       (optional)
 *
 * On success, fires `onCreated(consumable)` so the caller can splice the new
 * doc into its product picker. On error, surfaces the server error inline and
 * leaves the modal open so the user can retry without losing their input.
 */
export interface QuickCreateConsumableModalProps {
  open: boolean;
  /** Pre-select a type when opened from a context that already knows it. */
  defaultType?: ConsumableType;
  onCancel: () => void;
  onCreated: (created: ConsumableBase) => void;
}

const TYPE_OPTIONS: { id: ConsumableType; labelKey: string }[] = [
  { id: 'chemical',   labelKey: 'consumables.catalog.filters.type.chemical' },
  { id: 'ppe',        labelKey: 'consumables.catalog.filters.type.ppe' },
  { id: 'fertilizer', labelKey: 'consumables.catalog.filters.type.fertilizer' },
  { id: 'diesel',     labelKey: 'consumables.catalog.filters.type.diesel' },
];

const QuickCreateConsumableModal: React.FC<QuickCreateConsumableModalProps> = ({
  open,
  defaultType,
  onCancel,
  onCreated,
}) => {
  const { t } = useTranslation();
  const [consumableType, setConsumableType] = useState<ConsumableType>(defaultType ?? 'chemical');
  const [productName, setProductName] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the modal is re-opened so a fresh open never inherits
  // stale state from a previous cancel/error.
  useEffect(() => {
    if (open) {
      setConsumableType(defaultType ?? 'chemical');
      setProductName('');
      setCategory('');
      setSubmitting(false);
      setError(null);
    }
  }, [open, defaultType]);

  const canSubmit = productName.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: Parameters<typeof quickCreateConsumable>[0] = {
        consumableType,
        productName: productName.trim(),
      };
      const trimmedCategory = category.trim();
      if (trimmedCategory) body.category = trimmedCategory;
      const created = await quickCreateConsumable(body);
      onCreated(created);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <Modal show={open} onHide={onCancel} title={t('consumables.procurement.quickCreate.title')}>
      <Modal.Body>
        {error && (
          <div className="pat-banner pat-banner--danger" role="alert" data-testid="quick-create-error">
            <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
            <div className="pat-banner__body">
              <div className="pat-banner__title">{error}</div>
            </div>
          </div>
        )}
        <FormSection id="quick-create" title="">
          <Field label={t('consumables.procurement.quickCreate.field.consumableType')} required>
            <div role="radiogroup" aria-label={t('consumables.procurement.quickCreate.field.consumableType')}>
              {TYPE_OPTIONS.map(opt => {
                const inputId = `quick-create-type-${opt.id}`;
                return (
                  <div key={opt.id} className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id={inputId}
                      name="quick-create-consumable-type"
                      value={opt.id}
                      checked={consumableType === opt.id}
                      onChange={() => setConsumableType(opt.id)}
                    />
                    <label className="form-check-label" htmlFor={inputId}>
                      {t(opt.labelKey)}
                    </label>
                  </div>
                );
              })}
            </div>
          </Field>

          <Field
            label={t('consumables.procurement.quickCreate.field.productName')}
            required
            controlId="quick-create-product-name"
          >
            <input
              type="text"
              id="quick-create-product-name"
              className="pat-input"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              required
            />
          </Field>

          <Field
            label={t('consumables.procurement.quickCreate.field.category')}
            controlId="quick-create-category"
          >
            <input
              type="text"
              id="quick-create-category"
              className="pat-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </Field>
        </FormSection>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          onClick={onCancel}
          disabled={submitting}
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {t('consumables.procurement.quickCreate.submit')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuickCreateConsumableModal;
